import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

const imageKeys = [
    'section_hero_image',
    'section_imgbox_image',
];

const AboutCMS = () => {
    const { code } = useParams();
    const formRef = useRef(null);

    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        section_hero_title: "",
        section_hero_description: "",
        section_hero_button_text: "",
        section_hero_button_link: "",
        section_imgbox_title: "",
        section_imgbox_description: "",
        section_imgbox_button_text: "",
        section_imgbox_button_link: "",
        section_imgbox_image_position: "",
        section_spaces_title: "",
        section_spaces_button_text: "",
        section_spaces_button_link: ""
    });

    const [images, setImages] = useState({});
    const [previews, setPreviews] = useState({});
    const [activeTab, setActiveTab] = useState('section_hero');

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e, key) => {
        const file = e.target.files[0];
        if (file) {
            setImages(prev => ({ ...prev, [key]: file }));
            setPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
        }
    };

    useEffect(() => {
        const fetchCMS = async () => {
            try {
                const res = await api.get(`/cms/${code}`);
                if (res.status === 200) {
                    const data = res.data.cms;

                    const previewObj = {};
                    imageKeys.forEach(key => {
                        if (data[key]) {
                            previewObj[key] = `${res.data.base_url}${data[key]}`;
                        }
                    });

                    setPreviews(previewObj);

                    const {
                        section_hero_title,
                        section_hero_description,
                        section_hero_button_text,
                        section_hero_button_link,
                        section_imgbox_title,
                        section_imgbox_description,
                        section_imgbox_button_text,
                        section_imgbox_button_link,
                        section_imgbox_image_position,
                        section_spaces_title,
                        section_spaces_button_text,
                        section_spaces_button_link,
                    } = data;

                    setFormData({
                        section_hero_title,
                        section_hero_description,
                        section_hero_button_text,
                        section_hero_button_link,
                        section_imgbox_title,
                        section_imgbox_description,
                        section_imgbox_button_text,
                        section_imgbox_button_link,
                        section_imgbox_image_position,
                        section_spaces_title,
                        section_spaces_button_text,
                        section_spaces_button_link
                    });
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchCMS();

        if (formRef.current && window.$.fn.validate) {

            window.$.validator.addMethod(
                "ckeditorRequired",
                (value) =>
                    value.replace(/<[^>]*>/g, "").trim().length > 0,
                "This field is required"
            );

            window.$(formRef.current).validate({
                ignore: ".ck-editor *",
                rules: {
                    section_hero_title: { required: false },
                    section_hero_description: { ckeditorRequired: true },
                    section_hero_button_text: { required: true },
                    section_hero_button_link: { required: true },

                    section_imgbox_title: { required: true },
                    section_imgbox_description: { ckeditorRequired: true },
                    section_imgbox_button_text: { required: true },
                    section_imgbox_image_position: { required: true },

                    section_spaces_title: { required: true },
                    section_spaces_button_text: { ckeditorRequired: true },
                    section_spaces_button_link: { required: true },
                },
                messages: {},
                errorElement: 'label',
                validClass: 'is-valid',
                errorClass: 'is-invalid text-danger',
                highlight: (element, errorClass, validClass) => {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: (element, errorClass, validClass) => {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [code]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => data.append(key, value));
            Object.entries(images).forEach(([key, file]) => data.append(key, file));

            try {
                setSubmitting(true);
                const res = await api.put(`/cms/${code}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                showToast('success', res.data.message, 'Success');
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const renderImageInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label>{label}</label>
            <input type="file" className="form-control" onChange={e => handleImageChange(e, name)} />
            {previews[name] && <img src={previews[name]} alt="preview" width={100} className="mt-2" />}
        </div>
    );

    const renderTextInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label className="form-label">{label}</label>
            <input
                type="text"
                className="form-control"
                name={name}
                value={formData[name]}
                onChange={handleChange}
            />
        </div>
    );

    const renderTextareaInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label className="form-label">{label}</label>
            <CKEditor
                key={name}
                editor={ClassicEditor}
                config={{
                    toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'link', 'code', 'codeBlock',
                        '|', 'bulletedList', 'numberedList'
                    ]
                }}
                data={formData[name]}
                onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData(prev => ({ ...prev, [name]: data }));
                }}
            />
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'section_hero':
                return (
                    <>
                        {renderTextInput('Title', 'section_hero_title')}
                        {renderTextareaInput('Description', 'section_hero_description')}
                        {renderTextInput('Button Text', 'section_hero_button_text')}
                        {renderTextInput('Button Link', 'section_hero_button_link')}
                        {renderImageInput('Hero Image', 'section_hero_image')}
                    </>
                );
            case 'section_image_box':
                return (
                    <>
                        {renderTextInput('Title', 'section_imgbox_title')}
                        {renderTextareaInput('Description', 'section_imgbox_description')}
                        {renderTextInput('Button Text', 'section_imgbox_button_text')}
                        {renderTextInput('Button Link', 'section_imgbox_button_link')}
                        {renderTextInput('Image Position', 'section_imgbox_image_position')}
                        {renderImageInput('Hero Image', 'section_imgbox_image')}
                    </>
                );
            case 'section_space_cards':
                return (
                    <>
                        {renderTextInput('Title', 'section_spaces_title')}
                        {renderTextInput('Button Text', 'section_spaces_button_text')}
                        {renderTextInput('Button Link', 'section_spaces_button_link')}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/cms/about`}>About Page</Link>
                    </li>
                </ol>
            </div>

            <div className="card">
                <div className="card-header">
                    <h4 className="card-title">About CMS</h4>
                </div>
                <div className="card-body">
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_hero' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_hero')}
                            >
                                Section Hero
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_image_box' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_image_box')}
                            >
                                Section Image Box
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_space_cards' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_space_cards')}
                            >
                                Section Space Cards
                            </button>
                        </li>
                    </ul>
                    <form ref={formRef} noValidate>
                        <div className="row">{renderTabContent()}</div>
                        <div className="pt-3">
                            <button
                                type="button"
                                onClick={handleFormSubmit}
                                className="btn btn-sm btn-primary me-2"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <i className="spinner-border spinner-border-sm me-1"></i>
                                        Submit
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-floppy-disk me-1"></i>
                                        Submit
                                    </>
                                )}
                            </button>
                            <Link to={ROUTES.ROOT} className="btn btn-sm btn-danger light">
                                <i className="fas fa-times-circle"></i>&nbsp;Cancel
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AboutCMS;
